/**
 * The guided tour's content (finding #28).
 *
 * Rewritten from a stepper into an annotated example of the reader's own screens. A stepper asked
 * somebody to read six paragraphs and then go and find the things they described; an annotated picture
 * of the screen puts the explanation on the thing it is about, so the reader recognises it when they
 * get there. The numbered notes are the content and live here as data; the mock markup they point at
 * lives in the page, because it is layout.
 *
 * Three tracks, because the three people using this app are doing genuinely different jobs: the coach
 * builds and watches, the guardian sets up and pays, the athlete trains.
 */
export type TutorialTrack = "coach" | "guardian" | "athlete";

export const TRACK_LABELS: Record<TutorialTrack, string> = {
  coach: "What you see as the coach",
  guardian: "What you see as a parent",
  athlete: "What your skater sees",
};

/**
 * Which tracks each role may read.
 *
 * Nobody is asked who they are -- the app already knows -- so this decides what is *offered* beyond a
 * reader's own screens. A guardian explaining the workout to an eight-year-old needs the skater's
 * version; a coach answering "what does a parent actually see when they log in" needs both. A skater
 * gets only their own: the coach's roster and the family's billing are not theirs to read about, and
 * offering them would be inviting a nine-year-old to go looking.
 */
export const VISIBLE_TRACKS: Record<TutorialTrack, TutorialTrack[]> = {
  coach: ["coach", "guardian", "athlete"],
  guardian: ["guardian", "athlete"],
  athlete: ["athlete"],
};

/** How the page introduces itself once it knows who is reading. Nobody is asked to pick. */
export const TRACK_GREETING: Record<TutorialTrack, string> = {
  coach: "This is your side of the app. Tap any number to see what that part does.",
  guardian: "This is what you see, and what it is for. Tap any number to find out more.",
  athlete: "This is your training screen. Tap any number to see what it does.",
};

/** One numbered note on the example screen. The number itself comes from its position. */
export interface ExampleNote {
  /** Stable id, so the mock markup can place a marker without depending on ordering. */
  id: string;
  /** The short label on the marker's own line, before it is expanded. */
  summary: string;
  /** What this part is for, in the reader's terms. Not a description of the pixels. */
  body: string;
  /** Where to go and do it for real. Omitted where there is nowhere to send them. */
  link?: { to: string; label: string };
}

/** One mocked-up screen, with its notes. */
export interface ExampleScene {
  id: string;
  /** What this is a picture of, named the way the app names it. */
  title: string;
  /** Why this screen exists, in one line, above the picture. */
  intro: string;
  notes: ExampleNote[];
}

const COACH_SCENES: ExampleScene[] = [
  {
    id: "coach-roster",
    title: "Your clients",
    intro: "Where you land when you sign in, and the one screen that tells you who needs something.",
    notes: [
      {
        id: "coach-attention",
        summary: "Needs your attention",
        body: "An athlete reporting pain mid-session, and anyone who has a plan but has stopped logging sessions. Both come to you rather than waiting to be found — a flagged injury is the one thing in this app you should never have to go looking for. \"Mark as seen\" clears it once you have dealt with it.",
        link: { to: "/dashboard", label: "Open your clients" },
      },
      {
        id: "coach-filters",
        summary: "Search and filters",
        body: "Search by name, or narrow to athletes with no published plan — the quickest way to find who you still owe work to. Families who have cancelled or stopped are hidden until you ask for them, so the list stays the people you are actually coaching.",
      },
      {
        id: "coach-client-card",
        summary: "An athlete's row",
        body: "Their plan, whether they have trained lately, and anything happening to their subscription — a downgrade they have chosen, or a date they are leaving on. Open takes you to everything about them: their plan, your private notes, their session history.",
      },
      {
        id: "coach-invite",
        summary: "Adding an athlete",
        body: "An invite creates the athlete straight away, so you can build their plan while they are still signing up. For a child the invite goes to the guardian, who accepts, consents and sets up payment. Adding a second skater to a family you already coach is the same button — pick the family instead of typing a new address.",
      },
    ],
  },
  {
    id: "coach-builder",
    title: "Building a plan",
    intro: "Exercises come from your library, so a cue you fix once is fixed everywhere it appears.",
    notes: [
      {
        id: "coach-library-link",
        summary: "Everything points at your library",
        body: "Add the exercises you actually use once, with a demo video and your cues. Plans reference them rather than copying them, so improving a cue updates every plan using that exercise — including ones you wrote months ago.",
        link: { to: "/library", label: "Open your library" },
      },
      {
        id: "coach-prescription",
        summary: "The prescription",
        body: "Sets and reps, or a hold in seconds, plus load and the rests. A blank rest means none — the athlete goes straight on — so a pause you want them to take is one you have to type.",
      },
      {
        id: "coach-circuit",
        summary: "Circuits",
        body: "Some work goes round: three rounds of four exercises, rather than three sets of each in turn. Add a circuit the same way you add an exercise, then fill it. The athlete's screen counts rounds instead of sets and loops back to the top on its own.",
      },
      {
        id: "coach-publish",
        summary: "Draft, then published",
        body: "A draft is invisible to the family, so you can leave one half-finished safely. Publishing replaces whatever was live. If you spot a mistake afterwards, unpublish it back to a draft rather than copying it — the family simply has no current plan for the minute it takes you to fix.",
      },
      {
        id: "coach-sessions",
        summary: "What actually happened",
        body: "Every set is recorded separately, so you can see somebody holding up for two sets and falling off a cliff on the third — which an average across the exercise hides completely. The rating on each row is how hard they said the whole session felt.",
      },
    ],
  },
];

const GUARDIAN_SCENES: ExampleScene[] = [
  {
    id: "guardian-home",
    title: "Your training",
    intro: "The screen you land on, and the two questions it answers: did they do it, and what am I paying?",
    notes: [
      {
        id: "guardian-status",
        summary: "Whether they trained",
        body: "Under each athlete: when they last trained and how much this week. If they reported something wrong during a session, that shows here too — you find out from this screen rather than from your child mentioning it days later.",
        link: { to: "/family", label: "Go to your athletes" },
      },
      {
        id: "guardian-billing-line",
        summary: "What you pay",
        body: "One line, so you are not hunting through settings to find out what leaves your account each month. The full breakdown — per athlete, with any sibling discount — is on your settings page.",
      },
      {
        id: "guardian-view-plan",
        summary: "Their plan",
        body: "What the coach has set, how long it takes, and their whole training history. If your skater does not have their own login yet, this is also where you run the session with them.",
      },
      {
        id: "guardian-login",
        summary: "Giving them their own login",
        body: "Optional, and worth understanding before you do it: once your child has their own username and password, they log their own sessions and you become read-only for that. You keep everything else — their plan, their history, and your private thread with the coach. It cannot be undone.",
      },
    ],
  },
  {
    id: "guardian-settings",
    title: "Money, consent and leaving",
    intro: "Everything about the account, with the things you can never undo kept at the bottom.",
    notes: [
      {
        id: "guardian-next-payment",
        summary: "Your next payment",
        body: "The amount and the date, spelled out. You can change your card, read every invoice, and see exactly what each athlete is on.",
        link: { to: "/family/settings", label: "Open your settings" },
      },
      {
        id: "guardian-change-plan",
        summary: "Changing plan",
        body: "Per athlete, so moving one child to a cheaper plan does not move their sibling. A dearer plan starts straight away and you pay the difference for the rest of the month; a cheaper one waits until the month you have already paid for runs out. Either way you confirm the price before anything is charged, and a change you have queued can be undone before it lands.",
      },
      {
        id: "guardian-consent",
        summary: "What you agreed to",
        body: "The date you accepted the Privacy Policy and Terms, and gave parental consent, with both documents linked so you can read them again.",
      },
      {
        id: "guardian-leaving",
        summary: "Stopping",
        body: "Stopping one athlete's training is not the same as cancelling the family's subscription, and the buttons say which is which. Either way you keep access until the end of the month you have paid for, nothing is deleted, and while a cancellation is pending you can still change your mind.",
      },
    ],
  },
];

const ATHLETE_SCENES: ExampleScene[] = [
  {
    id: "athlete-session",
    title: "Doing your session",
    intro: "One exercise at a time, with the app counting for you.",
    notes: [
      {
        id: "athlete-start",
        summary: "Before you start",
        body: "It tells you roughly how long the whole thing takes and how many blocks there are, so you know what you are getting into. Then a countdown, and straight into the first exercise.",
        link: { to: "/family", label: "Go to your training" },
      },
      {
        id: "athlete-position",
        summary: "Where you are",
        body: "The big line at the top says which exercise and which set. In a circuit it counts rounds instead, and still tells you how far through the whole session you are.",
      },
      {
        id: "athlete-reps",
        summary: "What you actually did",
        body: "The number your coach asked for is already in the box. If you did exactly that, just hit Complete. If you managed more or fewer, change it — the real number is far more useful to your coach than the one they hoped for.",
      },
      {
        id: "athlete-hurt",
        summary: "If something hurts",
        body: "Press it. It goes to your coach straight away, on its own, whether or not you finish the session — and you can choose to stop there. Nobody is annoyed about it. That is the entire reason the button exists.",
      },
      {
        id: "athlete-rest",
        summary: "Rest",
        body: "When your coach has set a rest, the screen becomes a countdown. Between exercises it shows you what is coming next with its video, so you can watch it rather than stare at a number. You can always skip.",
      },
      {
        id: "athlete-rpe",
        summary: "How hard was that?",
        body: "At the end you rate the whole session from 1 to 10 — 1 is very easy, 10 is as hard as you could go. Be honest: it is how your coach decides whether next week should be harder or easier. Then you see what you got through.",
      },
    ],
  },
];

export const EXAMPLE_SCENES: Record<TutorialTrack, ExampleScene[]> = {
  coach: COACH_SCENES,
  guardian: GUARDIAN_SCENES,
  athlete: ATHLETE_SCENES,
};

/** Every note on a track, in the order they are numbered across its scenes. */
export function notesForTrack(track: TutorialTrack): ExampleNote[] {
  return EXAMPLE_SCENES[track].flatMap((scene) => scene.notes);
}

/** Remembers that somebody has been through the tour, so the "new here?" prompt stops after once. */
const SEEN_KEY = "aa.tutorial.seen";

export function markTutorialSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // A browser with storage disabled just gets the prompt every time, which is a far smaller problem
    // than a page that throws on load.
  }
}

export function hasSeenTutorial(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}
